import { InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Tooltip,
  message,
} from 'antd';
import moment from 'moment';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { createSyntheticApi, updateApiKey } from '../../../api/apiKey';
import { fetchGroupsWithDescription } from '../../../api/groups';
import {
  APIKeyDetails,
  APIKeyPayload,
  UpdateAPIKeyPayload,
} from '../../../constants/types';
import { supervisorpfUserGroup } from '../../../constants/userGroup';
import { SHORT_DATE_TIME_FORMAT, disabledTime } from '../../../utils/date';
import { formatErrorMessage } from '../../../utils/errorMessage';
import { ModalFooterBand, StyledModal2 } from '../../../pages/styles';
import { ErrorMessage } from '../APIKeyInstance/style';
import {
  CheckboxContainer,
  StyledCheckboxGroup,
  StyledCheckboxItem,
  StyledFormItem,
} from '../style';
import { checkDuplicateKeyName, createValidateKeyName } from './validators';

const { Option } = Select;

const DatePickerField = styled(DatePicker)`
  width: 100%;
`;

const UserGroupsContainer = styled(CheckboxContainer)`
  height: 180px;
  margin-top: 2px;
`;

const NoMarginFormItem = styled(StyledFormItem)`
  margin-bottom: 0;
`;

const GroupInfoIcon = styled(InfoCircleOutlined)`
  color: ${({ theme }) => theme.palette.primary.main};
`;

const UserGroupsLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const CustomKeyHintList = styled.ul`
  margin-top: 4px;
  padding-left: 18px;
`;

const NameCounterSuffix = styled.span`
  color: ${({ theme }) => theme.palette.text.grey};
  font-size: 13px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
`;

const CompactInput = styled(Input)`
  &.ant-input-affix-wrapper {
    display: flex;
    align-items: center;
    padding: 4px 11px;
    height: 36px;
  }
  /* Counter the global .ant-input { height: 36px } rule so the inner input
   * sits naturally inside the wrapper instead of stretching it. */
  &.ant-input-affix-wrapper > input.ant-input {
    height: auto;
    line-height: 1.5715;
    padding: 0;
  }
  &.ant-input-affix-wrapper .ant-input-suffix {
    display: inline-flex;
    align-items: center;
    margin-left: 8px;
  }
`;

const ModalForm = styled(Form)`
  display: block;
`;

const ScrollableFormContent = styled.div`
  /* Fixed working height so the modal stays one consistent size: switching to
   * the "Custom" key field scrolls inside this area instead of resizing the
   * modal. min() caps the height to the viewport on short screens, and on
   * tall/high-res monitors it settles at 540px instead of stretching to 80vh. */
  height: min(590px, calc(80vh - 70px));
  overflow-y: auto;
  padding: 20px 24px 12px;
`;

export type APIKeyFormMode = 'new' | 'update' | 'rotate';

interface Props {
  visible: boolean;
  mode: APIKeyFormMode;
  source?: APIKeyDetails;
  onCancel: () => void;
  onSuccess: (result: { id?: string; apiKey?: string }) => void;
}

interface FormValues {
  keyName: string;
  keyDescription?: string;
  keycloakGroups: string[];
  activeFrom: moment.Moment;
  expiresAt: moment.Moment;
  generationMethod?: 'generate' | 'custom';
  customApiKey?: string;
}

type LegacyAPIKeySource = APIKeyDetails & {
  keyName?: string;
  keyDescription?: string;
  name1?: string;
  name2?: string;
};

const APIKeyFormModal: FC<Props> = ({
  visible,
  mode,
  source,
  onCancel,
  onSuccess,
}) => {
  const intl = useIntl();
  const [form] = Form.useForm<FormValues>();
  const validateKeyName = createValidateKeyName(intl);
  const duplicateKeyNameValidator = checkDuplicateKeyName(intl, {
    ignoreApiKeyId: mode === 'update' ? source?.id : undefined,
  });
  const validateDuplicateKeyName = async (_rule: any, keyName?: string) => {
    const normalizedInput = (keyName ?? '').trim().toLowerCase();
    const normalizedSourceName = sourceKeyName.trim().toLowerCase();

    // In update mode, unchanged name should always be valid.
    if (
      mode === 'update' &&
      normalizedInput &&
      normalizedInput === normalizedSourceName
    ) {
      return Promise.resolve();
    }

    return duplicateKeyNameValidator(_rule, keyName);
  };
  const [groupOptions, setGroupOptions] = useState<
    { value: string; label: string; description?: string }[]
  >([]);
  const [groupsError, setGroupsError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generationMethod, setGenerationMethod] = useState<
    'generate' | 'custom'
  >('generate');
  const [keyNameCount, setKeyNameCount] = useState(0);
  const [descriptionCount, setDescriptionCount] = useState(0);
  const sourceKeyName =
    (source as LegacyAPIKeySource | undefined)?.name ||
    (source as LegacyAPIKeySource | undefined)?.keyName ||
    (source as LegacyAPIKeySource | undefined)?.name1 ||
    '';
  const sourceDescription =
    (source as LegacyAPIKeySource | undefined)?.description ||
    (source as LegacyAPIKeySource | undefined)?.keyDescription ||
    (source as LegacyAPIKeySource | undefined)?.name2 ||
    '';

  const initialValues: Partial<FormValues> = (() => {
    if (mode === 'new') {
      return {
        keyName: '',
        keyDescription: '',
        keycloakGroups: [],
        customApiKey: '',
        activeFrom: moment(),
        expiresAt: moment().add(1, 'years'),
        generationMethod: 'generate',
      };
    }
    if (mode === 'update' && source) {
      return {
        keyName: sourceKeyName,
        keyDescription: sourceDescription,
        keycloakGroups: source.groups ?? [],
        activeFrom: moment(source.activeFrom),
        expiresAt: moment(source.expireAt),
      };
    }
    if (mode === 'rotate' && source) {
      return {
        keyName: sourceKeyName,
        keyDescription: sourceDescription,
        keycloakGroups: source.groups ?? [],
        activeFrom: moment(),
        expiresAt: moment().add(1, 'years'),
        generationMethod: 'generate',
      };
    }
    return {};
  })();

  useEffect(() => {
    if (!visible) return;
    fetchGroupsWithDescription().then(groups => {
      setGroupOptions(
        groups
          .filter(g => g.name !== supervisorpfUserGroup)
          .map(g => ({
            value: g.name,
            label: g.name,
            description: g.description,
          })),
      );
    });
  }, [visible]);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      form.setFieldsValue(initialValues);
      setGenerationMethod('generate');
      setGroupsError(undefined);
      setKeyNameCount((initialValues.keyName ?? '').length);
      setDescriptionCount((initialValues.keyDescription ?? '').length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, mode, source]);

  const disabledStartDate = (current: any) =>
    current && current < moment().startOf('day');

  const disabledEndDate = (current: any) => {
    if (!current) return false;
    const start = form.getFieldValue('activeFrom') as moment.Moment | undefined;
    if (start) {
      return (
        current <= moment(start).endOf('day') ||
        current <= moment().startOf('day')
      );
    }
    return current <= moment().endOf('day');
  };

  const handleFinish = async (values: FormValues) => {
    if (!values.keycloakGroups || values.keycloakGroups.length === 0) {
      setGroupsError(
        intl.formatMessage({ id: 'ApiKeys.form.validation.userGroups' }),
      );
      return;
    }
    setGroupsError(undefined);
    setIsSubmitting(true);

    try {
      if (mode === 'update' && source?.id) {
        const payload: UpdateAPIKeyPayload = {
          name: values.keyName.trim(),
          description: values.keyDescription?.trim() ?? '',
          groups: values.keycloakGroups,
          activeFrom: values.activeFrom.utc().format(),
          expireAt: values.expiresAt.utc().format(),
        };
        await updateApiKey(source.id, payload);
        onSuccess({ id: source.id });
        return;
      }

      const payload: APIKeyPayload = {
        keyName: values.keyName.trim(),
        keyDescription: values.keyDescription?.trim() ?? '',
        groups: values.keycloakGroups,
        activeFrom: values.activeFrom.utc().format(),
        expiresAt: values.expiresAt.utc().format(),
      };
      if (
        mode !== 'update' &&
        generationMethod === 'custom' &&
        values.customApiKey
      ) {
        payload.apiKey = values.customApiKey.trim();
      }
      const { data } = await createSyntheticApi(payload);
      const newKey =
        data.apikey ??
        (generationMethod === 'custom'
          ? values.customApiKey?.trim()
          : undefined);
      onSuccess({ id: data.id, apiKey: newKey });
    } catch (error: any) {
      message.error(
        formatErrorMessage(error?.data?.errors?.[0] ?? error?.data?.errorCode),
        2,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <StyledModal2
      visible={visible}
      footer={null}
      onCancel={onCancel}
      destroyOnClose
      centered
      width={620}
      title={
        mode === 'update'
          ? intl.formatMessage({ id: 'ApiKeys.modal.title.update' })
          : mode === 'rotate'
            ? intl.formatMessage({ id: 'ApiKeys.modal.title.rotate' })
            : intl.formatMessage({ id: 'ApiKeys.modal.apiKeygenerate.title' })
      }
      data-testid={`apikey-form-modal-${mode}`}
    >
      <ModalForm
        layout="vertical"
        form={form}
        initialValues={initialValues}
        onFinish={handleFinish}
        data-testid={`apikey-form-${mode}`}
      >
        <ScrollableFormContent>
          <StyledFormItem
            label={<FormattedMessage id="ApiKeys.form.label.keyName" />}
            name="keyName"
            validateTrigger={['onBlur', 'onChange']}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'ApiKeys.form.validation.keyName',
                }),
              },
              { validator: validateKeyName },
              {
                validator: validateDuplicateKeyName,
                validateTrigger: ['onBlur'],
              },
            ]}
          >
            <CompactInput
              maxLength={30}
              suffix={
                <NameCounterSuffix>{`${keyNameCount}/30`}</NameCounterSuffix>
              }
              onChange={event => setKeyNameCount(event.target.value.length)}
              data-testid="apikey-form-name"
            />
          </StyledFormItem>

          <StyledFormItem
            label={<FormattedMessage id="ApiKeys.form.label.description" />}
            name="keyDescription"
          >
            <CompactInput
              maxLength={30}
              suffix={
                <NameCounterSuffix>{`${descriptionCount}/30`}</NameCounterSuffix>
              }
              onChange={event => setDescriptionCount(event.target.value.length)}
              data-testid="apikey-form-description"
            />
          </StyledFormItem>

          <Row gutter={20}>
            <Col span={12}>
              <StyledFormItem
                label={<FormattedMessage id="ApiKeys.modal.label.activefrom" />}
                name="activeFrom"
                validateFirst
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({
                      id: 'ApiKeys.GenerateApikeyModal.activeFrom.validation',
                    }),
                  },
                  {
                    validator: async (_rule, value) => {
                      const expiresAt = form.getFieldValue('expiresAt');
                      if (expiresAt && moment(value).isAfter(moment(expiresAt))) {
                        throw new Error(
                          intl.formatMessage({
                            id: 'ApiKeys.form.validation.activeFromBeforeActiveUntil',
                          }),
                        );
                      }
                    },
                  },
                ]}
              >
                <DatePickerField
                  showTime
                  className="date-picker"
                  dropdownClassName="apikey-datepicker-dropdown"
                  format={SHORT_DATE_TIME_FORMAT}
                  disabledDate={disabledStartDate}
                  data-testid="apikey-form-activeFrom"
                />
              </StyledFormItem>
            </Col>
            <Col span={12}>
              <StyledFormItem
                label={<FormattedMessage id="ApiKeys.modal.label.activeuntil" />}
                name="expiresAt"
                validateFirst
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({
                      id: 'ApiKeys.GenerateApikeyModal.expiryFrom.validation',
                    }),
                  },
                  {
                    validator: async (_rule, value) => {
                      if (moment(value).isBefore(moment())) {
                        throw new Error(
                          intl.formatMessage({
                            id: 'ApiKeys.form.validation.activeUntilFuture',
                          }),
                        );
                      }
                    },
                  },
                ]}
              >
                <DatePickerField
                  showTime
                  className="date-picker"
                  dropdownClassName="apikey-datepicker-dropdown"
                  format={SHORT_DATE_TIME_FORMAT}
                  disabledDate={disabledEndDate}
                  disabledTime={disabledTime}
                  onChange={() => form.validateFields(['activeFrom'])}
                  data-testid="apikey-form-expiresAt"
                />
              </StyledFormItem>
            </Col>
          </Row>

          <NoMarginFormItem
            label={
              <UserGroupsLabel>
                <FormattedMessage id="ApiKeys.form.label.userGroups" />
                <Tooltip
                  title={intl.formatMessage({
                    id: 'ApiKeys.form.tooltip.userGroups',
                  })}
                >
                  <GroupInfoIcon />
                </Tooltip>
              </UserGroupsLabel>
            }
            required
          >
            <UserGroupsContainer>
              <NoMarginFormItem name="keycloakGroups">
                <StyledCheckboxGroup
                  onChange={() => setGroupsError(undefined)}
                  className="without-boarder"
                >
                  {groupOptions.map((group, index) => (
                    <StyledCheckboxItem key={group.value}>
                      <Checkbox
                        data-testid={`apikey-form-group-${index}`}
                        value={group.value}
                      >
                        {group.label}
                      </Checkbox>
                      {group.description && (
                        <Tooltip title={group.description}>
                          <GroupInfoIcon />
                        </Tooltip>
                      )}
                    </StyledCheckboxItem>
                  ))}
                </StyledCheckboxGroup>
              </NoMarginFormItem>
            </UserGroupsContainer>
            {groupsError && (
              <ErrorMessage>
                <span className="icon-wraper">
                  <WarningOutlined />
                </span>
                {groupsError}
              </ErrorMessage>
            )}
          </NoMarginFormItem>

          {mode === 'update' && source?.masked && (
            <StyledFormItem
              label={intl.formatMessage({ id: 'ApiKeys.column.apiKey' })}
            >
              <Input
                value={source.masked}
                readOnly
                disabled
                data-testid="apikey-form-masked"
              />
            </StyledFormItem>
          )}

          {mode !== 'update' && (
            <>
              <StyledFormItem
                label={intl.formatMessage({
                  id: 'ApiKeys.modal.generationMethod.label',
                })}
                name="generationMethod"
              >
                <Select
                  onChange={value =>
                    setGenerationMethod(value as 'generate' | 'custom')
                  }
                  data-testid="apikey-form-generation-method"
                >
                  <Option value="generate">
                    {intl.formatMessage({
                      id: 'ApiKeys.modal.generationMethod.generate',
                    })}
                  </Option>
                  <Option value="custom">
                    {intl.formatMessage({
                      id: 'ApiKeys.modal.generationMethod.custom',
                    })}
                  </Option>
                </Select>
              </StyledFormItem>

              {generationMethod === 'custom' && (
                <StyledFormItem
                  label={intl.formatMessage({ id: 'ApiKeys.column.apiKey' })}
                  name="customApiKey"
                  rules={[
                    {
                      required: true,
                      validator: async (_rule, value) => {
                        const v = (value ?? '').trim();
                        if (!v) {
                          throw new Error(
                            intl.formatMessage({
                              id: 'ApiKeys.modal.input.validation.required',
                            }),
                          );
                        }
                        if (v.length < 20 || v.length > 128) {
                          throw new Error(
                            intl.formatMessage({
                              id: 'ApiKeys.modal.input.validation.maxCharacters',
                            }),
                          );
                        }
                        if (!/^[a-zA-Z0-9-]+$/.test(v)) {
                          throw new Error(
                            intl.formatMessage({
                              id: 'ApiKeys.modal.input.validation.onlyAlphanumeric',
                            }),
                          );
                        }
                      },
                    },
                  ]}
                  extra={
                    <CustomKeyHintList>
                      <li>
                        {intl.formatMessage({
                          id: 'ApiKeys.modal.input.helpText.maxCharacters',
                        })}
                      </li>
                      <li>
                        {intl.formatMessage({
                          id: 'ApiKeys.modal.input.helpText.onlyAlphanumeric',
                        })}
                      </li>
                    </CustomKeyHintList>
                  }
                >
                  <Input maxLength={128} data-testid="apikey-form-custom-key" />
                </StyledFormItem>
              )}
            </>
          )}
        </ScrollableFormContent>

        <ModalFooterBand>
          <Button
            type="default"
            size="middle"
            onClick={onCancel}
            data-testid="apikey-form-cancel"
          >
            <FormattedMessage id="Global.cancel" />
          </Button>
          <Button
            type="primary"
            size="middle"
            htmlType="submit"
            disabled={isSubmitting}
            data-testid="apikey-form-submit"
          >
            {mode === 'update'
              ? intl.formatMessage({ id: 'ApiKeys.modal.btn.update' })
              : mode === 'rotate'
                ? intl.formatMessage({ id: 'ApiKeys.modal.btn.rotate' })
                : intl.formatMessage({ id: 'ApiKeys.modal.btn.create' })}
          </Button>
        </ModalFooterBand>
      </ModalForm>
    </StyledModal2>
  );
};

export default APIKeyFormModal;
